'use client'
import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useLanguage } from '@/lib/context/LanguageContext'
import { services } from '@/lib/data/services'
import type { Lang } from '@/lib/i18n'
import { FadeInUp } from '@/components/ui/FadeInUp'
import SectionHeader from '@/components/ui/SectionHeader'
import { ServiceIcon } from './ServiceIcons'
import { ServiceIllustration, NetworkBg } from './ServiceIllustrations'

export default function Services() {
  const { t, lang } = useLanguage()
  const l = lang as Lang
  // Hover-driven: the card under the cursor expands and reveals its content.
  // The last hovered card stays open after the cursor leaves the strip — the
  // strip never collapses to an all-inactive state once mounted.
  const [active, setActive] = useState<number>(0)

  return (
    <section id="services" className="py-16 md:py-24 lg:py-32 border-b border-ink/5">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <SectionHeader num="02" label={t.services.section} />

        <FadeInUp delay={0.05}>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-8 md:mb-16">
            <h2 className="font-display font-black text-3xl sm:text-4xl md:text-5xl text-ink leading-tight max-w-xl">
              {t.services.heading}
            </h2>
            <p className="text-muted text-sm max-w-xs leading-relaxed">{t.services.sub}</p>
          </div>
        </FadeInUp>

        {/* ── Desktop: Agenzo-style horizontal expand-on-hover card strip ── */}
        <FadeInUp delay={0.1}>
          <div className="hidden md:flex gap-2" style={{ height: 430 }}>
            {services.map((svc, i) => (
              <div
                key={svc.number}
                className="relative overflow-hidden rounded-2xl"
                style={{
                  flexGrow: active === i ? 5 : 1,
                  flexShrink: 1,
                  flexBasis: 0,
                  minWidth: 70,
                  transition: 'flex-grow 0.48s cubic-bezier(0.21, 0.47, 0.32, 0.98)',
                }}
                onMouseEnter={() => setActive(i)}
              >
                {/* Background */}
                <div
                  className="absolute inset-0 rounded-2xl"
                  style={{
                    background:
                      active === i
                        ? 'linear-gradient(150deg, #0b1629 0%, #08205a 100%)'
                        : '#ffffff',
                    border: '1px solid rgba(11,22,41,0.07)',
                    transition: 'background 0.4s ease',
                  }}
                />

                {/* SVG network background — only on active */}
                <AnimatePresence>
                  {active === i && (
                    <motion.div
                      className="absolute inset-0"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.35 }}
                    >
                      <NetworkBg />
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* ── Inactive content ── */}
                <AnimatePresence>
                  {active !== i && (
                    <motion.div
                      className="absolute inset-0 overflow-hidden"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.16 }}
                    >
                      <div className="absolute top-5 left-0 right-0 flex flex-col items-center gap-1.5 px-2">
                        <ServiceIcon idx={i} size={30} active={false} />
                        <p className="text-[7px] font-bold uppercase tracking-[0.1em] text-muted/60 text-center leading-tight w-full break-words">
                          {svc.proof[l]}
                        </p>
                      </div>

                      {/* Rotate whole block — preserves Persian ligatures */}
                      <div
                        className="absolute left-1/2 top-1/2 pointer-events-none select-none"
                        style={{
                          transform: `translate(-50%, -50%) rotate(${l === 'fa' ? '90deg' : '-90deg'})`,
                          width: 320,
                        }}
                      >
                        <h3 className="font-display font-black text-[11px] text-ink text-center whitespace-nowrap leading-none">
                          {svc.title[l]}
                        </h3>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* ── Active content ── */}
                <AnimatePresence>
                  {active === i && (
                    <motion.div
                      className="absolute inset-0 flex flex-col p-7 sm:p-8"
                      initial={{ opacity: 0 }}
                      // Enter: delay 0.48s so the card has finished expanding
                      // (flex-grow transition is 0.48s) before content appears.
                      // Otherwise longer titles (cards 2, 4) wrap to 2 lines
                      // while the card is narrow, pushing the SVG illustration
                      // up. As the card widens the title collapses back to one
                      // line and the SVG drops — visible as a layout jump.
                      // Showing content only at final width eliminates reflow.
                      animate={{ opacity: 1, transition: { duration: 0.24, delay: 0.48 } }}
                      // Exit: instant. Any fade duration leaves the high-contrast
                      // cyan accents (e.g. bar-chart line) visible as ghosts on
                      // the whitening card. duration: 0 unmounts immediately.
                      exit={{ opacity: 0, transition: { duration: 0 } }}
                    >
                      <span
                        className="w-fit text-[10px] font-bold uppercase tracking-[0.2em] px-3 py-1.5 rounded-full leading-none flex-shrink-0"
                        style={{
                          background: 'rgba(255,255,255,0.1)',
                          color: 'rgba(255,255,255,0.6)',
                        }}
                      >
                        {svc.proof[l]}
                      </span>

                      <ServiceIllustration idx={i} />

                      <div className="flex items-end justify-between gap-6 flex-shrink-0">
                        <div className="min-w-0 flex-1">
                          <h3 className="font-display font-black text-2xl sm:text-[1.75rem] text-white leading-tight mb-3">
                            {svc.title[l]}
                          </h3>
                          <div className="text-white/45 text-xs sm:text-sm leading-relaxed space-y-1">
                            {svc.description[l].split(' • ').map((line, idx) => (
                              <p key={idx}>{line}</p>
                            ))}
                          </div>
                        </div>
                        <ServiceIcon idx={i} size={72} active={true} />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </FadeInUp>

        {/* ── Mobile: compact card stack ── */}
        <div className="md:hidden flex flex-col gap-3">
          {services.map((svc) => (
            <div
              key={svc.number}
              className="rounded-2xl p-5 border border-ink/5 bg-white shadow-sm"
            >
              <div className="flex items-start justify-between mb-3">
                <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted">
                  {svc.proof[l]}
                </span>
                <ServiceIcon idx={services.indexOf(svc)} size={40} active={false} />
              </div>
              <h3 className="font-display font-black text-lg text-ink mb-2">{svc.title[l]}</h3>
              <div className="text-xs text-muted leading-relaxed space-y-1">
                {svc.description[l].split(' • ').map((line, idx) => (
                  <p key={idx}>{line}</p>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
